import { Fragment, type CSSProperties, type JSX } from 'react';

/**
 * Shared registrar-only login + activity audit table (House Rule O). One row per
 * sign-in — when it started, how long it stayed active (last-seen heartbeat), how
 * it ended, who/where — with a tidy line of the session's recent activity beneath.
 *
 * Purely presentational: the consuming portal fetches its own sessions and passes
 * them in. Identical across portals — only `accent` (the brand colour) varies; the
 * status tones (active/idle/signed-out) are fixed and semantic, per the CCAANZ
 * portal standard.
 */

export interface AuditActivity {
  action: string;
  detail: unknown;
  at: string;
}

export interface AuditSession {
  email: string;
  tier: string | null;
  login_at: string;
  last_seen_at: string;
  ended_at: string | null;
  end_reason: string | null;
  ip: string | null;
  user_agent: string | null;
  /** Recent actions in this session — newest-first, capped. */
  activity: AuditActivity[];
}

interface Props {
  sessions: AuditSession[];
  /** Portal brand colour — the only permitted per-portal difference. */
  accent?: string;
  /** Optional override for the empty state. */
  emptyLabel?: string;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-NZ', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtDuration(fromIso: string, toIso: string): string {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (isNaN(ms) || ms < 0) return '—';
  const mins = Math.round(ms / 60000);
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Signed out (explicit) / Active (seen recently) / Inactive (idle, no logout). */
function status(s: AuditSession): { label: string; tone: string } {
  if (s.end_reason === 'logout' && s.ended_at) {
    return { label: `Signed out ${fmtWhen(s.ended_at)}`, tone: '#6b7280' };
  }
  const idleMs = Date.now() - new Date(s.last_seen_at).getTime();
  if (idleMs < 15 * 60 * 1000) return { label: 'Active now', tone: '#166534' };
  return { label: `Inactive · last seen ${fmtWhen(s.last_seen_at)}`, tone: '#b45309' };
}

/** A short, friendly device label from the User-Agent. */
function device(ua: string | null): string {
  if (!ua) return '—';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  const os = /iPhone|iPad|iOS/.test(ua)
    ? 'iOS'
    : /Macintosh|Mac OS/.test(ua)
      ? 'Mac'
      : /Windows/.test(ua)
        ? 'Windows'
        : /Android/.test(ua)
          ? 'Android'
          : '';
  return os ? `${browser} · ${os}` : browser;
}

/**
 * A tidy one-line label for an activity event. The portal controls the wording
 * by passing `{ title }` (or `{ label }`) in the recorded detail; otherwise we
 * fall back to the raw action token. Never surfaces third-party PII — detail is
 * IDs/titles only by the recorder's contract.
 */
function describeActivity(a: AuditActivity): string {
  const d = a.detail;
  if (d && typeof d === 'object') {
    const rec = d as Record<string, unknown>;
    if (typeof rec.title === 'string') return rec.title;
    if (typeof rec.label === 'string') return rec.label;
  }
  return a.action;
}

export function LoginHistoryList({ sessions, accent, emptyLabel }: Props): JSX.Element {
  const th: CSSProperties = {
    textAlign: 'left',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#6b7280',
    padding: '0 12px 6px 0',
    borderBottom: '1px solid #e0ddd6',
    whiteSpace: 'nowrap',
  };
  const td: CSSProperties = { fontSize: 13, padding: '7px 12px 7px 0', verticalAlign: 'top' };
  const activityTone = accent ?? '#6b7280';

  if (sessions.length === 0) {
    return (
      <p style={{ fontSize: 13, color: '#9ca3af', margin: '8px 0' }}>
        {emptyLabel ?? 'No sign-ins recorded yet.'}
      </p>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>Signed in</th>
            <th style={th}>Active for</th>
            <th style={th}>Who / device</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s, i) => {
            const st = status(s);
            return (
              <Fragment key={i}>
                <tr style={{ borderBottom: s.activity.length > 0 ? 'none' : '1px solid #f0efe9' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 600 }}>{fmtWhen(s.login_at)}</div>
                    <div style={{ fontSize: 12, color: st.tone }}>{st.label}</div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: '#555' }}>
                    {fmtDuration(s.login_at, s.ended_at ?? s.last_seen_at)}
                  </td>
                  <td style={{ ...td, color: '#555' }}>
                    {s.email}
                    <span style={{ color: '#9ca3af' }}>
                      {' · '}
                      {device(s.user_agent)}
                      {s.ip ? ` · ${s.ip}` : ''}
                    </span>
                  </td>
                </tr>
                {s.activity.length > 0 && (
                  <tr style={{ borderBottom: '1px solid #f0efe9' }}>
                    <td colSpan={3} style={{ fontSize: 12, color: '#9ca3af', padding: '0 0 8px' }}>
                      <span style={{ color: activityTone, fontWeight: 600 }}>Activity:</span>{' '}
                      <span style={{ color: '#6b7280' }}>
                        {s.activity.slice(0, 6).map(describeActivity).join(' · ')}
                        {s.activity.length > 6 ? ` +${s.activity.length - 6} more` : ''}
                      </span>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
